// ============================================================================
// GAME ITEMS SERVICE
// ============================================================================
// Handles PKCash purchases for in-game items and real-time Firestore sync
// so all players at the table see animations
//  ENTERPRISE: iOS WebView compatibility fixes

// ============================================================================

import { db } from './firebase';
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import walletService from './walletService';
import { GameItem, GameItemAction, ActiveAnimation, getGameItem, ALL_GAME_ITEMS, getItemPrice, getItemCooldown } from '../types/gameItemsTypes';

// ============================================================================
//  ENTERPRISE: iOS WebView Compatibility Helpers

// ============================================================================

/**
 * Detect if running in iOS WebView (Capacitor)
 */
function isIOSWebView(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isCapacitor = !!(window as any).Capacitor;
  return isIOS && isCapacitor;
}

/**
 * Safe currency operation with iOS compatibility
 * iOS sometimes has issues with very small floating point operations
 */
function safeCurrencyRound(value: number): number {
  return Math.round(value * 100) / 100;
}

class GameItemsService {
  // Track cooldowns locally per user
  private cooldowns: Map<string, number> = new Map();
  
  // Active subscriptions for cleanup
  private activeSubscriptions: Map<string, () => void> = new Map();

  // ============================================================================
  // PURCHASE & USE ITEM
  // ============================================================================

  /**
   * Purchase and use a game item
   * Deducts PKCash from wallet and creates Firestore animation record
   *  UPDATED: Supports restricted state pricing

   *  ENTERPRISE: iOS WebView compatibility

   */
  async useItem(
    tableId: string,
    itemId: string,
    senderId: string,
    senderName: string,
    targetId: string,
    targetName: string,
    isRestrictedState: boolean = false //  NEW: Optional state flag

  ): Promise<{ success: boolean; error?: string; newBalance?: number }> {
    try {
      // 1. Get the item details
      const item = getGameItem(itemId);
      if (!item) {
        return { success: false, error: 'Item not found' };
      }

      //  ENTERPRISE: Get state-adjusted price with proper rounding for iOS

      const itemPrice = safeCurrencyRound(getItemPrice(itemId, isRestrictedState));
      const itemCooldown = getItemCooldown(itemId, isRestrictedState);

      // 2. Check cooldown (using state-adjusted cooldown)
      const cooldownKey = `${senderId}-${itemId}`;
      const lastUsed = this.cooldowns.get(cooldownKey) || 0;
      const now = Date.now();
      
      if (now - lastUsed < itemCooldown) {
        const remaining = Math.ceil((itemCooldown - (now - lastUsed)) / 1000);
        return { success: false, error: `Cooldown: ${remaining}s remaining` };
      }

      //  ENTERPRISE: Log before purchase attempt (helps debug iOS issues)

      console.log(`🎁 [GameItems] Purchase starting: ${item.name} (${item.emoji}) for $${itemPrice.toFixed(2)} by ${senderId.substring(0,8)}`);

      // 3. Purchase item via walletService (state-adjusted price). Free
      // items (taunts) skip the wallet round-trip entirely - no charge,
      // no transaction record, just the animation.
      let newBalance: number | undefined;
      if (itemPrice > 0) {
        const purchaseResult = await walletService.purchaseGameItem(
          senderId,
          itemPrice,
          `${item.name} (${item.emoji})`,
          tableId
        );
        if (!purchaseResult.success) {
          console.error(`❌ [GameItems] Purchase failed: ${purchaseResult.error}`);
          return { success: false, error: purchaseResult.error };
        }
        newBalance = purchaseResult.newBalance;
      }

      // 4. Create animation record in Firestore
      //  ENTERPRISE: Separate from purchase - if this fails, purchase still succeeded

      try {
        const expiresAt = new Date(now + item.duration + 1000); // Extra 1s buffer
        
        const actionData = {
          tableId,
          itemId,
          senderId,
          senderName,
          targetId,
          targetName,
          pricePaid: itemPrice, //  Track actual price paid

          isRestrictedState, //  Track if restricted state

          timestamp: Timestamp.now(),
          expiresAt: Timestamp.fromDate(expiresAt),
        };

        console.log('🎁 [GameItems] Creating animation record:', actionData);
        
        const docRef = await addDoc(collection(db, 'gameItemActions'), actionData);
        console.log('🎁 [GameItems] Animation record created with ID:', docRef.id);
        
      } catch (firestoreError: any) {
        //  ENTERPRISE: Don't fail the whole operation if animation record fails

        // Purchase was successful, animation just won't show
        console.error('❌ [GameItems] Firestore animation error (purchase still succeeded):', firestoreError.message);
      }

      // 5. Set cooldown
      this.cooldowns.set(cooldownKey, now);

      console.log(`🎁 [GameItems] ${senderName} used ${item.emoji} ${item.name} on ${targetName} ($${itemPrice.toFixed(2)})`);
      
      return { success: true, newBalance };
      
    } catch (error: any) {
      console.error('❌ [GameItems] Error using item:', error);
      
      //  ENTERPRISE: Better error messages for iOS

      let errorMessage = 'Failed to use item';
      
      if (error.message?.includes('network') || error.message?.includes('Network')) {
        errorMessage = 'Network error - check your connection';
      } else if (error.message?.includes('Insufficient')) {
        errorMessage = error.message;
      } else if (error.message?.includes('permission') || error.message?.includes('Permission')) {
        errorMessage = 'Permission denied - please try again';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  }

  // ============================================================================
  // LISTEN TO ACTIVE ANIMATIONS
  // ============================================================================

  /**
   * Subscribe to active animations for a table
   * Returns unsubscribe function
   */
  subscribeToAnimations(
    tableId: string,
    onAnimations: (animations: ActiveAnimation[]) => void
  ): () => void {
    // Clean up existing subscription for this table
    const existingUnsub = this.activeSubscriptions.get(tableId);
    if (existingUnsub) {
      existingUnsub();
    }

    console.log('🎬 [GameItems] Subscribing to animations for table:', tableId);
    
    // SIMPLE query - just filter by tableId, NO orderBy to avoid needing index
    const q = query(
      collection(db, 'gameItemActions'),
      where('tableId', '==', tableId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const animations: ActiveAnimation[] = [];
      
      //  ENTERPRISE: Reduced logging to prevent console spam

      if (snapshot.size > 0) {
        console.log('🎬 [GameItems] Snapshot received, docs:', snapshot.size);
      }
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        const item = getGameItem(data.itemId);
        if (!item) {
          return;
        }
        
        const expiresAt = data.expiresAt?.toDate?.()?.getTime() || 0;
        const isExpired = expiresAt <= now;
        
        // Only include non-expired animations
        if (!isExpired) {
          animations.push({
            id: doc.id,
            itemId: data.itemId,
            item,
            senderId: data.senderId,
            senderName: data.senderName,
            targetId: data.targetId,
            targetName: data.targetName,
            startTime: data.timestamp?.toDate?.()?.getTime() || Date.now(),
          });
        }
      });

      // Sort by startTime (newest first) - client-side sorting
      animations.sort((a, b) => b.startTime - a.startTime);
      
      if (animations.length > 0) {
        console.log('🎬 [GameItems] Active animations count:', animations.length);
      }
      onAnimations(animations);
    }, (error) => {
      console.error('❌ [GameItems] Animation subscription error:', error);
      console.error('❌ [GameItems] Error details:', error.message);
      onAnimations([]);
    });

    this.activeSubscriptions.set(tableId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from table animations
   */
  unsubscribeFromAnimations(tableId: string): void {
    const unsub = this.activeSubscriptions.get(tableId);
    if (unsub) {
      unsub();
      this.activeSubscriptions.delete(tableId);
    }
  }

  // ============================================================================
  // CLEANUP EXPIRED ANIMATIONS
  // ============================================================================

  /**
   * Clean up expired animation records (call periodically or on table leave)
   */
  async cleanupExpiredAnimations(tableId: string): Promise<void> {
    try {
      const now = Timestamp.now();
      const q = query(
        collection(db, 'gameItemActions'),
        where('tableId', '==', tableId)
      );

      const snapshot = await getDocs(q);
      const deletePromises: Promise<void>[] = [];
      
      snapshot.docs.forEach(doc => {
        const expiresAt = doc.data().expiresAt?.toDate?.()?.getTime() || 0;
        if (expiresAt <= Date.now()) {
          deletePromises.push(deleteDoc(doc.ref));
        }
      });
      
      await Promise.all(deletePromises);

      if (deletePromises.length > 0) {
        console.log(`🧹 [GameItems] Cleaned up ${deletePromises.length} expired animations`);
      }
    } catch (error) {
      console.error('❌ [GameItems] Cleanup error:', error);
    }
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Check if user can afford an item
   */
  async canAfford(userId: string, itemId: string): Promise<boolean> {
    const item = getGameItem(itemId);
    if (!item) return false;

    const wallet = await walletService.getWallet(userId);
    if (!wallet) return false;

    return wallet.pkCash >= item.price;
  }

  /**
   * Get remaining cooldown for an item (in ms)
   */
  getCooldownRemaining(userId: string, itemId: string): number {
    const item = getGameItem(itemId);
    if (!item) return 0;

    const cooldownKey = `${userId}-${itemId}`;
    const lastUsed = this.cooldowns.get(cooldownKey) || 0;
    const elapsed = Date.now() - lastUsed;
    
    return Math.max(0, item.cooldown - elapsed);
  }

  /**
   * Check if item is on cooldown
   */
  isOnCooldown(userId: string, itemId: string): boolean {
    return this.getCooldownRemaining(userId, itemId) > 0;
  }

  /**
   * Get all available items
   */
  getAllItems(): GameItem[] {
    return ALL_GAME_ITEMS;
  }

  /**
   * Get items by category
   */
  getItemsByCategory(category: 'throwable' | 'self'): GameItem[] {
    return ALL_GAME_ITEMS.filter(item => item.category === category);
  }
}

export const gameItemsService = new GameItemsService();
export default gameItemsService;